import React, { useRef, useState } from 'react';

import AvatarRenderer from './AvatarRenderer';
import {
  createAvatarCreation,
  updateAvatarCreation,
} from '../services/avatarApi';
import { exportAvatarPng } from '../utils/exportAvatarPng';

export default function AvatarSavePanel({
  avatar,
  creation,
  onSaved,
  onBusyChange,
}) {
  const exportRef = useRef(null);
  const savingRef = useRef(false);

  const [name, setName] = useState(creation?.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const isExisting = Boolean(creation?._id);

  async function handleSave(saveAsNew = false) {
    if (savingRef.current) return;

    setError('');
    setMessage('');

    const cleanedName = name
      .normalize('NFC')
      .trim()
      .replace(/ +/g, ' ');

    if (
      !cleanedName ||
      Array.from(cleanedName).length > 40 ||
      /[\u0000-\u001F\u007F-\u009F]/u.test(cleanedName)
    ) {
      setError('Enter a name of 1–40 characters, without line breaks.');
      return;
    }

    savingRef.current = true;
    setSaving(true);
    onBusyChange?.(true);

    try {
      const image = await exportAvatarPng(exportRef.current);

      const payload = {
        name: cleanedName,
        config: avatar,
        image,
      };

      const saved =
        isExisting && !saveAsNew
          ? await updateAvatarCreation(creation._id, {
              ...payload,
              revision: creation.revision,
            })
          : await createAvatarCreation(payload);

      setName(saved.name);
      onSaved(saved);

      setMessage(
        isExisting && !saveAsNew
          ? 'Changes saved. Apply again to update your profile picture.'
          : 'Creation saved. Your profile picture has not changed.'
      );
    } catch (err) {
      const status = err.response?.status;

      if (status === 409) {
        setError(
          'This creation changed elsewhere. Reopen it before updating, or choose Save as new.'
        );
      } else {
        setError(
          err.response?.data?.error ||
          err.message ||
          'Could not save your avatar. Please try again.'
        );
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
      onBusyChange?.(false);
    }
  }

  return (
    <section
      className="avatar-save"
      aria-label="Name and save your avatar"
      aria-busy={saving}
    >
      {/* Dedicated square SVG, separate from the cropped editor preview. */}
      <div className="avatar-save__export" aria-hidden="true">
        <AvatarRenderer
          ref={exportRef}
          avatar={avatar}
          size={512}
          viewBox="0 0 400 400"
          showBackground={true}
          rounded={false}
        />
      </div>

      <form
        className="avatar-save__form"
        onSubmit={(event) => {
          event.preventDefault();
          handleSave(false);
        }}
      >
        <label htmlFor="avatar-creation-name">
          Avatar name
        </label>

        <input
          id="avatar-creation-name"
          type="text"
          value={name}
          disabled={saving}
          placeholder="Give your avatar a name"
          autoComplete="off"
          aria-describedby="avatar-save-help"
          onChange={(event) => {
            setName(event.target.value);
            setError('');
            setMessage('');
          }}
        />

        <p id="avatar-save-help" className="avatar-save__help">
          Save to your collection. Applying it to your profile is a
          separate action.
        </p>

        {error && (
          <p className="avatar-save__error" role="alert">
            {error}
          </p>
        )}

        {message && (
          <p className="avatar-save__message" role="status">
            {message}
          </p>
        )}

        <div className="avatar-save__actions">
          <button
            type="submit"
            className="avatar-save__primary"
            disabled={saving}
          >
            {saving
              ? 'Saving…'
              : isExisting
                ? 'Save changes'
                : 'Save creation'}
          </button>

          {isExisting && (
            <button
              type="button"
              className="avatar-save__secondary"
              disabled={saving}
              onClick={() => handleSave(true)}
            >
              Save as new
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
